import { useEffect, useState, type ComponentType } from 'react'
import {
  BarChart3,
  Blocks,
  Ellipsis,
  LayoutDashboard,
  LogOut,
  Moon,
  ShieldCheck,
  Sun,
  Truck,
  UserCog,
  Users,
  Wrench,
  X,
} from 'lucide-react'
import { canAdministerAccounts, useAuth } from '../context/AuthContext'
import { useLiveClock } from '../hooks/useLiveClock'
import type { Theme } from '../hooks/useTheme'
import { formatWallClock } from '../lib/format'
import {
  activeDestinationKey,
  mobileNavSections,
  type MobileDestination,
} from '../lib/mobileNav'
import { navigate, useRoute } from '../lib/router'
import type { AuthUser } from '../types/identity'
import { Avatar } from './Avatar'
import type { SettingsTarget } from './TopNav'

/** One icon per category — the same five the dashboard tab bar names. */
const DESTINATION_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  overview: LayoutDashboard,
  insights: BarChart3,
  suppliers: Truck,
  modules: Blocks,
  tools: Wrench,
}

interface MobileTabBarProps {
  theme: Theme
  onToggleTheme: () => void
  displayName?: string
  avatar?: string | null
  roleLabel?: string
  onSignOut?: () => void
  onOpenSettings?: (target: SettingsTarget) => void
  /** Test seam: render the bar for a specific account without a live session. */
  user?: AuthUser | null
  /** Test seam: open the More sheet on the first render. */
  initialMoreOpen?: boolean
}

function SheetRow({
  icon: Icon,
  label,
  hint,
  onClick,
  tone = 'default',
}: {
  icon: ComponentType<{ className?: string }>
  label: string
  hint?: string
  onClick: () => void
  tone?: 'default' | 'danger'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors',
        'hover:bg-slate-50 dark:hover:bg-white/5',
        tone === 'danger'
          ? 'text-salsa-600 dark:text-salsa-300'
          : 'text-slate-800 dark:text-slate-100',
      ].join(' ')}
    >
      <Icon className="h-4 w-4 shrink-0 text-salsa-500" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{label}</span>
        {hint && (
          <span className="block text-[11px] leading-snug text-slate-500 dark:text-slate-400">
            {hint}
          </span>
        )}
      </span>
    </button>
  )
}

/**
 * Phone navigation (D-106).
 *
 * A fixed bottom bar with the four most-used screens this account may open, plus a
 * **More** sheet for the rest: the leftover categories, Account/Profile settings,
 * User Management (only for account administrators), the theme switch and Sign out.
 *
 * Everything here is capability-driven — the bar asks `mobileNavSections`, which asks
 * the backend-owned capability list. It is hidden from `lg` up, where the top bar and
 * the dashboard tab bar already do this job.
 */
export function MobileTabBar({
  theme,
  onToggleTheme,
  displayName,
  avatar,
  roleLabel,
  onSignOut,
  onOpenSettings,
  user,
  initialMoreOpen = false,
}: MobileTabBarProps) {
  const { user: sessionUser } = useAuth()
  const account = user !== undefined ? user : sessionUser
  const route = useRoute()
  const now = useLiveClock()
  const [moreOpen, setMoreOpen] = useState(initialMoreOpen)

  // A navigation always ends the sheet; leaving it open over the new screen would
  // hide the very numbers the person just asked for.
  useEffect(() => {
    setMoreOpen(false)
  }, [route.path])

  useEffect(() => {
    if (!moreOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMoreOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [moreOpen])

  const { primary, overflow } = mobileNavSections(account)
  if (!account || primary.length === 0) return null

  const active = activeDestinationKey(route.path)
  const showUserManagement = canAdministerAccounts(account)
  const openSettings = (target: SettingsTarget) => {
    setMoreOpen(false)
    onOpenSettings?.(target)
  }
  const go = (destination: MobileDestination) => {
    setMoreOpen(false)
    navigate(destination.path)
  }

  return (
    <>
      <nav
        aria-label="Primary"
        className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden dark:border-white/10 dark:bg-ink-950/95"
      >
        <div className="mx-auto flex w-full max-w-[1600px] items-stretch">
          {primary.map((destination) => {
            const Icon = DESTINATION_ICONS[destination.key] ?? LayoutDashboard
            const selected = active === destination.key
            return (
              <button
                key={destination.key}
                type="button"
                onClick={() => go(destination)}
                aria-current={selected ? 'page' : undefined}
                aria-label={destination.label}
                className={[
                  'flex min-h-[3.25rem] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5',
                  'text-[10px] font-semibold transition-colors',
                  selected
                    ? 'text-salsa-600 dark:text-salsa-300'
                    : 'text-slate-500 active:bg-slate-100 dark:text-slate-400 dark:active:bg-white/5',
                ].join(' ')}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                <span className="w-full truncate text-center">{destination.shortLabel}</span>
                <span
                  className={`h-0.5 w-6 rounded-full ${
                    selected ? 'bg-salsa-500' : 'bg-transparent'
                  }`}
                  aria-hidden="true"
                />
              </button>
            )
          })}

          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            className={[
              'flex min-h-[3.25rem] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5',
              'text-[10px] font-semibold transition-colors',
              moreOpen
                ? 'text-salsa-600 dark:text-salsa-300'
                : 'text-slate-500 active:bg-slate-100 dark:text-slate-400 dark:active:bg-white/5',
            ].join(' ')}
          >
            <Ellipsis className="h-5 w-5" aria-hidden="true" />
            <span className="w-full truncate text-center">More</span>
            <span className="h-0.5 w-6 rounded-full bg-transparent" aria-hidden="true" />
          </button>
        </div>
      </nav>

      {moreOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="More"
          className="fixed inset-0 z-50 flex items-end bg-slate-900/60 backdrop-blur-sm lg:hidden"
          onClick={(event) => {
            if (event.target === event.currentTarget) setMoreOpen(false)
          }}
        >
          <div className="pb-safe max-h-[85dvh] w-full overflow-y-auto rounded-t-3xl border-t border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-ink-900">
            <div className="flex items-center justify-between px-4 pt-3">
              <span
                className="mx-auto h-1 w-10 rounded-full bg-slate-300 dark:bg-white/20"
                aria-hidden="true"
              />
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                className="icon-button ml-2 h-8 w-8 shrink-0"
                aria-label="Close the menu"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 dark:border-white/5">
              <Avatar
                name={displayName ?? account.full_name ?? 'Store OS'}
                avatar={avatar ?? account.avatar}
              />
              <span className="min-w-0 leading-tight">
                <span className="block truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {displayName ?? account.full_name ?? 'Signed in'}
                </span>
                <span className="block truncate text-[11px] text-slate-500 dark:text-slate-400">
                  {roleLabel ?? account.role} · {formatWallClock(now)}
                </span>
              </span>
            </div>

            {overflow.length > 0 && (
              <div className="space-y-0.5 px-2 py-2">
                <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Screens
                </p>
                {overflow.map((destination) => (
                  <SheetRow
                    key={destination.key}
                    icon={DESTINATION_ICONS[destination.key] ?? LayoutDashboard}
                    label={destination.label}
                    onClick={() => go(destination)}
                  />
                ))}
              </div>
            )}

            <div className="space-y-0.5 border-t border-slate-100 px-2 py-2 dark:border-white/5">
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Your account
              </p>
              <SheetRow
                icon={ShieldCheck}
                label="Account Settings"
                hint="Password, e-mail address, two-factor"
                onClick={() => openSettings('account')}
              />
              <SheetRow
                icon={UserCog}
                label="Profile Settings"
                hint="Your name, language and notifications"
                onClick={() => openSettings('profile')}
              />
              {showUserManagement && (
                <SheetRow
                  icon={Users}
                  label="User Management"
                  hint="Accounts and access in your scope"
                  onClick={() => {
                    setMoreOpen(false)
                    navigate('/users')
                  }}
                />
              )}
              <SheetRow
                icon={theme === 'dark' ? Sun : Moon}
                label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
                onClick={onToggleTheme}
              />
              {onSignOut && (
                <SheetRow
                  icon={LogOut}
                  label="Sign out"
                  tone="danger"
                  onClick={() => {
                    setMoreOpen(false)
                    onSignOut()
                  }}
                />
              )}
            </div>

            <p className="px-4 pb-4 pt-1 text-center text-[10px] text-slate-400 dark:text-slate-500">
              Salsa Shop · Store OS
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default MobileTabBar

